import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DvDropdownFieldRenderer, DvDropdownFieldRendererParams } from '@devinco/ui/dds';
import { RouteNetwork } from '@features/map/interfaces/map-data';
import LayerColorUtils from '@features/map/utils/layer-color.utils';

export interface RouteNetworkStatusData extends RouteNetwork {
  Favorite?: boolean;
}

@Component({
  selector: 'app-route-network-status-renderer',
  templateUrl: './route-network-status.renderer.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './route-network-status.renderer.scss',
})
export class RouteNetworkStatusRenderer implements DvDropdownFieldRenderer, OnInit {
  private sanitizer = inject(DomSanitizer);

  params =
    input.required<
      DvDropdownFieldRendererParams<RouteNetworkStatusData, RouteNetworkStatusData, string>
    >();

  iconUrl = signal<SafeHtml | undefined>(undefined);

  highlightedName = computed(() => {
    const { data, searchTerm } = this.params();
    return this.highlightFullText(data.Name, searchTerm);
  });

  highlightedDescription = computed(() => {
    const { data, searchTerm } = this.params();
    return this.highlightFullText(data.Description, searchTerm);
  });

  highlightedMeta = computed(() => {
    const { data, searchTerm } = this.params();

    const association = data.Association
      ? this.highlightFullText(data.Association, searchTerm)
      : undefined;

    const county = data.CountyName
      ? this.highlightFullText(data.CountyName, searchTerm)
      : undefined;

    return {
      association,
      county,
      show: !!(data.Association || data.CountyName),
      showDot: !!(data.Association && data.CountyName),
    };
  });

  ngOnInit(): void {
    const rowData: RouteNetworkStatusData = this.params().data;

    this.iconUrl.set(
      LayerColorUtils.getRouteColorOrIcon(rowData.LastMaintainedTimeUTC, rowData.CustomerID, true),
    );
  }

  private highlightFullText(originalText: string | undefined, searchTerm?: string): SafeHtml {
    const text = originalText ?? '';

    if (!searchTerm || !searchTerm.trim()) {
      return this.sanitizer.bypassSecurityTrustHtml(text);
    }

    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');

    const result = text.replace(regex, (match) => `<span class="search-highlight">${match}</span>`);

    return this.sanitizer.bypassSecurityTrustHtml(result);
  }
}
